import { freeze } from 'immer';
import { get } from '../../lib/util';
import type { Events, TimeData } from './TimeData';
import type { LoadedCompartment } from './Core.cargo-allocation';
import type { LocationId } from '@seaber/common/core/entries';
import type { AssortmentGroupId, AssortmentId, OrderId } from '../../graphql';

export type DeriveCapacitiesAndTimesState = {
  prev: LocationId;
  /** Index of the PortCallArrivalEvent of the current port call */
  portCallArrivalIndex: number | null;
  arriveInPort: number;
  previousEnd: number;
  /** Infinity while the vessel does not define the dimension: unknown headroom caps nothing. */
  volumeCapacityAvailable: number;
  massCapacityAvailable: number;
  deckVolumeCapacityAvailable: number;
  /** Cargo aboard, tracked apart from the headroom above so it stays a real figure on a vessel
   * whose capacity is unknown, where capacity−headroom would be Infinity−Infinity. */
  massAboard: number;
  holdVolumeAboard: number;
  deckVolumeAboard: number;
  /** Σ on-board orders' minimum mass; vessel mass cap − this = available-at-minimum. */
  minMassUsed: number;
  minHoldVolumeUsed: number;
  minDeckVolumeUsed: number;
  loadedCompartmentsById: Map<number, LoadedCompartment>;
  totalHoursAtSea: number;
  totalHoursAtPort: number;
  ballastDistance: number;
  totalDistance: number;
  /** Legs whose distance could not be resolved, so they add nothing to `totalDistance`. */
  unresolvedLegs: number;
  bunkerCost: number;
  portCostEstimateTotal: number;
  totalMass: number;
  delays: number;
  constrainedDelays: number;
  tcCostTotal: number;
  cost: number;
  orderToLoadIndex: Map<number, number>;
  hasFirstShipmentEvent: boolean;
  concurrentPortHours: number;
  currentPortCallOperationsStart: number | null;
  /** Last discharge end in the current port call. */
  currentPortCallLastDischargeEnd: number | null;
  ordersOnBoard: number;
  /** Tracks which orders/assortments are currently on board */
  carriedAssortmentsByOrderId: Map<
    OrderId,
    { assortmentId: AssortmentId; assortmentGroupId: AssortmentGroupId | null }
  >;
};

/** Clones the state object.
 * cloneDeep is too costly because of large compartment and assortment objects */
export const cloneDeriveCapacitiesAndTimesState = (
  state: DeriveCapacitiesAndTimesState,
): DeriveCapacitiesAndTimesState => {
  const res = {
    ...state,
    carriedAssortmentsByOrderId: new Map(state.carriedAssortmentsByOrderId),
    loadedCompartmentsById: new Map(
      [...state.loadedCompartmentsById.entries()].map(([k, v]) => [
        k,
        { ...v, allocations: [...v.allocations] },
      ]),
    ),
    orderToLoadIndex: new Map(state.orderToLoadIndex),
  };
  return res;
};

/** A symbol that is used to represent a null value in the cache.
 * We can't use null itself because it is not a valid WeakMap key. */
export type NullSymbol = symbol & { __brand: 'NULL' };
export const NULL_SYMBOL = Symbol('NULL') as NullSymbol;

/** The cached entry carries the reservation signature it was derived against (see cacheTimeData). */
let cachedTimeData = new WeakMap<
  Events,
  WeakMap<
    TimeData | NullSymbol,
    readonly [TimeData, DeriveCapacitiesAndTimesState, string | null]
  >
>();

export const clearTimeDataCache = () => {
  cachedTimeData = new WeakMap();
};

const readCache = <T extends Events>(
  e: Events,
  prev: TimeData<T> | null,
):
  | readonly [TimeData<T>, DeriveCapacitiesAndTimesState, string | null]
  | null => {
  return (cachedTimeData.get(e)?.get(prev ?? NULL_SYMBOL) ?? null) as
    | readonly [TimeData<T>, DeriveCapacitiesAndTimesState, string | null]
    | null;
};

export const cacheTimeData = <T extends Events>(
  e: Events,
  prev: TimeData<T> | null,
  state: [TimeData<T>, DeriveCapacitiesAndTimesState],
  /** Signature of the later co-loaded orders this event reserved capacity for, or null when the
   * derived amount does not depend on later overlapping loads. A load's amount comes from
   * getMaxAllowedLoadAmount, which reserves the minimums of every later-overlapping load — a fact not
   * captured by the (event, prevTd) key. Storing the signature lets a read reject an entry derived
   * against a different co-loaded set (e.g. before another cargo was added after it). */
  reservationKey: string | null,
) => {
  // freeze ensures that we don't have any bugs which would mutate a cached value
  const value = [state[0], freeze(state[1], true), reservationKey] as const;
  if (!cachedTimeData.has(e)) {
    cachedTimeData.set(e, new WeakMap());
  }
  cachedTimeData.get(e)?.set(prev ?? NULL_SYMBOL, value);
};

/** Constructs initial part of derived timeline from cache.
 * Returns the index where the cache ends, the state object and the timedata array filled up to startIndex */
export function getCachedTimeData<EventType extends Events>(
  events: EventType[],
  /** event → its current later-overlapping reservation signature. A cached entry whose stored
   * signature differs was derived against a different co-loaded set and its amount is stale, so the
   * reusable prefix ends there. */
  reservationKeyByEvent: ReadonlyMap<Events, string | null>,
): {
  startIndex: number;
  state: DeriveCapacitiesAndTimesState | null;
  timeData: TimeData<EventType>[];
} {
  let lastCachedState: DeriveCapacitiesAndTimesState | null = null;
  let startIndex = 0;
  const arr = new Array<TimeData<EventType>>(events.length);
  for (; startIndex < events.length; startIndex++) {
    const event = events[startIndex];
    const prevTd = get(arr, startIndex - 1);
    const cachedResult = readCache(event, prevTd ?? null);
    if (
      cachedResult &&
      cachedResult[2] === (reservationKeyByEvent.get(event) ?? null)
    ) {
      arr[startIndex] = cachedResult[0];
      lastCachedState = cachedResult[1];
    } else {
      break;
    }
  }
  return {
    startIndex,
    state:
      lastCachedState && cloneDeriveCapacitiesAndTimesState(lastCachedState),
    timeData: arr,
  };
}
